import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { CircleNotch } from '@phosphor-icons/react'
import { toast } from 'sonner'

interface GameAttribute {
  id: string
  name: string
  description: string
  min: number
  max: number
  default: number
}

interface EditAttributeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  attributeData: GameAttribute | null
  onSave: (attributeData: Omit<GameAttribute, 'id'> & { id?: string }) => Promise<void>
}

// Inicial sugerido = min: atributo novo nao consome o orcamento da classe
// (starting_points) de saida — o aluno distribui para cima na criacao.
const EMPTY_FORM = {
  id: '',
  name: '',
  description: '',
  min: 1,
  max: 10,
  default: 1,
}

export function EditAttributeDialog({ open, onOpenChange, attributeData, onSave }: EditAttributeDialogProps) {
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [isSaving, setIsSaving] = useState(false)

  const isEditing = !!attributeData

  useEffect(() => {
    if (attributeData) {
      setFormData({
        id: attributeData.id,
        name: attributeData.name,
        description: attributeData.description || '',
        min: attributeData.min,
        max: attributeData.max,
        default: attributeData.default,
      })
    } else {
      setFormData(EMPTY_FORM)
    }
  }, [attributeData, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validação de faixa espelhada do backend, para feedback imediato
    if (formData.min >= formData.max) {
      toast.error('O mínimo deve ser menor que o máximo.')
      return
    }
    if (formData.default < formData.min || formData.default > formData.max) {
      toast.error('O valor inicial deve estar entre mínimo e máximo.')
      return
    }

    // Gerar ID se for novo atributo (mesma regra das classes)
    const attributeId = formData.id || formData.name.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')

    setIsSaving(true)
    try {
      await onSave({
        id: isEditing ? formData.id : attributeId,
        name: formData.name,
        description: formData.description,
        min: formData.min,
        max: formData.max,
        default: formData.default,
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Erro ao salvar atributo:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const numberField = (field: 'min' | 'max' | 'default', label: string) => (
    <div className="space-y-2">
      <Label htmlFor={`attr-${field}`}>{label}</Label>
      <Input
        id={`attr-${field}`}
        type="number"
        value={formData[field]}
        onChange={(e) => setFormData({ ...formData, [field]: parseInt(e.target.value) || 0 })}
        required
      />
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Editar Atributo' : 'Novo Atributo'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Personagens existentes mantêm os valores já distribuídos.'
                : 'Crie um novo atributo para os personagens da campanha.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="attr-name">Nome do Atributo *</Label>
              <Input
                id="attr-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Corpo, Mente, Carisma..."
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="attr-description">Descrição</Label>
              <Textarea
                id="attr-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="O que este atributo representa no jogo"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              {numberField('min', 'Mínimo *')}
              {numberField('max', 'Máximo *')}
              {numberField('default', 'Inicial *')}
            </div>
            <p className="text-xs text-muted-foreground">
              Faixa de valores que um personagem pode ter neste atributo; o inicial é o valor sugerido na criação.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <CircleNotch size={16} className="mr-2 animate-spin" />}
              {isEditing ? 'Salvar Alterações' : 'Criar Atributo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
